'use client'
import { Grid } from '@achieve/ascend'
import styles from './MainListArticlesCategory.module.scss'
import Section from 'components/Section'
import { _get } from 'utils/shared'
import { contentMap } from 'utils/contentful'
import { useEffect, useReducer } from 'react'
import { LinkButton, MediaImageStandard, Typography } from 'components/Contentful'
import { NavPills } from 'components/NavPills'
import { Eyebrow } from 'components/Eyebrow'
import { AchieveLink } from 'components/AchieveLink'
import { getSlug } from 'utils/conversions'
import { AUTHORS_ROUTE, LEARN_ROUTE } from 'constants/navigation'
import { AuthorAvatar } from 'components/AuthorAvatar'
import { Image } from 'components/Image'
import { getStrictValueContenfullRichText } from 'utils/shared'
import { mainProductConfig } from 'constants/mainProductConfig'
import classNames from 'classnames'

function MainListArticlesCategory({ content }) {
  const sectionVariationObject = {
    ...content?.fields?.experiment,
    variant: content?.fields?.experimentVariation,
  }
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const reducerFunction = (state, action) => {
    switch (action.type) {
      case 'setData':
        return { ...state, ...action.payload }
      case 'setActiveSection':
        return {
          ...state,
          ...{
            activeSection: action.payload,
            articles: state?.list[action.payload]?.fields?.relatedContentCollection?.items,
            link: contentMap(
              state?.list[action.payload]?.fields?.sectionContentsCollection?.items,
              ['link']
            )?.link,
          },
        }
      default:
        return state
    }
  }
  // eslint-disable-next-line no-unused-vars
  const [{ title, config, link, activeSection, list, articles }, dispatch] = useReducer(
    reducerFunction,
    {
      title: null,
      config: {},
      link: null,
      activeSection: 0,
      list: [],
      articles: [],
    }
  )

  useEffect(() => {
    if (!content) return
    const navData = _get(content, 'fields.sectionContents')
    let { title } = contentMap(navData, ['title'])
    const itemList = navData?.filter((item) => item.fields.__typename === 'PageSection')
    dispatch({
      type: 'setData',
      payload: {
        title: title?.textContent,
        config: content?.fields?.additionalConfiguration?.jsonContent,
        list: itemList,
      },
    })
    dispatch({
      type: 'setActiveSection',
      payload: 0,
    })
  }, [content])

  const getNavItems = list.map((item) => {
    const { title } = contentMap(item?.fields?.sectionContentsCollection?.items, ['title'])
    return { title: title?.textContent }
  })

  const renderMainArticle = () => {
    const article = articles?.[0]

    if (!article) return
    const isVideo = article.mainImage?.__typename === 'VideoPlayer'

    const authorPageRoute =
      `${AUTHORS_ROUTE}/` + (article?.author?.slug || getSlug(article?.author?.authorName))
    const hrefCategory = `${LEARN_ROUTE}/${getSlug(article.primaryCategory)}`
    const hrefArticle = `${hrefCategory}/${article?.slug}`
    return (
      <Grid container className={styles['main-articles-main']} data-testid="main-article-container">
        <Grid size={{ xs: 12, lg: 6 }} className="basis-full lg:basis-1/2">
          <div className={styles['main-articles-main-image']}>
            <MediaImageStandard
              alt={isVideo ? article.mainImage?.altText : article.mainImage?.mediaAltText}
              content={isVideo ? article.mainImage?.thumbnail : article.mainImage?.mediaContent}
              fetchPriority="high"
              preload={true}
              aspectRatio={2 / 1}
              data-testid="main-article-image"
            />
            {isVideo && (
              <div className={styles['play-button']}>
                <Image
                  alt={'Play Button'}
                  height={64}
                  src="/play-btn.svg"
                  width={64}
                  data-testid="main-article-play-button-svg"
                />
              </div>
            )}
          </div>
        </Grid>
        <Grid
          size={{ xs: 12, lg: 6 }}
          className={classNames(styles['main-article-info'], 'basis-full lg:basis-1/2')}
          data-testid="main-article-info-container"
        >
          <Grid container rowSpacing={2}>
            <Grid size={{ xs: 12 }}>
              <AchieveLink
                href={hrefCategory}
                className={styles['eyebrow-link']}
                data-testid="main-article-eyebrow-link"
              >
                <Eyebrow
                  className={classNames('text-start', styles['article-eyebrow'])}
                  content={article.primaryCategory}
                  component="h3"
                  data-testid="main-article-eyebrow"
                />
              </AchieveLink>
            </Grid>
            <Grid size={{ xs: 12 }} className={styles['main-article-title']}>
              <AchieveLink
                index={0}
                href={hrefArticle}
                track={{
                  list_name: `ARTICLE LINK`,
                  position: 0,
                  click_id: `${
                    mainProductConfig[siteEnv || 'achieve']?.['MainProduct']
                  } | ${getStrictValueContenfullRichText(article.title)}`,
                  click_text: getStrictValueContenfullRichText(article.title),
                  content_type: article.contentType,
                  click_url: hrefArticle,
                  nav_link_section: 'Article',
                  track_event: 'link_click',
                  click_type: 'Link Click',
                  event_action: 'link_click',
                  event_type: 'link_click',
                  sectionVariationObject,
                }}
                className={styles['card-link']}
                data-testid="main-article-title-link"
              >
                <Typography
                  variant="headingMd"
                  content={article.title}
                  data-testid="main-article-title"
                />
              </AchieveLink>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="bodySm" content={article.hook} data-testid="main-article-hook" />
            </Grid>
            {article?.author && (
              <Grid size={{ xs: 12 }} data-testid="main-article-info-author-container">
                <AuthorAvatar
                  authorPhoto={article?.author?.authorPhoto}
                  index={0}
                  href={authorPageRoute}
                  authorName={article?.author?.authorName}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const renderListArticles = () => {
    const secondaries = articles?.slice(1, 4)
    if (!secondaries) return
    return (
      <Grid
        container
        // columnSpacing={{ lg: 2 }}
        className={styles['secondaries-articles']}
        data-testid="list-articles-container"
      >
        {secondaries.map((secondaryArticle, index) => {
          const isVideo = secondaryArticle.mainImage?.__typename === 'VideoPlayer'
          const hrefCategory = `${LEARN_ROUTE}/${getSlug(secondaryArticle.primaryCategory)}`
          const hrefArticle = `${hrefCategory}/${secondaryArticle?.slug}`
          return (
            <Grid
              size={{ xs: 12, lg: 4 }}
              key={secondaryArticle?.sys?.id}
              className={classNames(
                styles['secondaries-articles-item'],
                'basis-full lg:basis-1/3',
                index !== 0 && 'pl-4'
              )}
            >
              <div className={styles['secondary-image-container']}>
                <MediaImageStandard
                  className={styles['image']}
                  alt={
                    isVideo
                      ? secondaryArticle.mainImage?.altText
                      : secondaryArticle.mainImage?.mediaAltText
                  }
                  content={
                    isVideo
                      ? secondaryArticle.mainImage?.thumbnail
                      : secondaryArticle.mainImage?.mediaContent
                  }
                  layout="fixed"
                  objectFit="cover"
                  fetchPriority="high"
                  preload={true}
                  width={85}
                  height={85}
                  data-testid="secondary-article-image"
                />
                {isVideo && (
                  <div className={styles['play-button-secondary']}>
                    <Image alt={'Play Button'} height={64} src="/play-btn.svg" width={64} />
                  </div>
                )}
              </div>

              <div
                className={styles['secondaries-articles-item-info']}
                data-testid="secondary-article-info-container"
              >
                <AchieveLink
                  href={hrefCategory}
                  className={styles['eyebrow-link']}
                  data-testid="secondary-article-eyebrow-link"
                >
                  <Eyebrow
                    className={classNames('text-start', styles['article-eyebrow'])}
                    content={secondaryArticle.primaryCategory}
                    component="h3"
                    data-testid="secondary-article-eyebrow"
                  />
                </AchieveLink>
                <div className={styles['secondaries-articles-item-title']}>
                  <AchieveLink
                    href={hrefArticle}
                    track={{
                      list_name: `ARTICLE LINK`,
                      position: index,
                      click_id: `${
                        mainProductConfig[siteEnv || 'achieve']?.['MainProduct']
                      } | ${getStrictValueContenfullRichText(secondaryArticle.title)}`,
                      click_text: getStrictValueContenfullRichText(secondaryArticle.title),
                      content_type: secondaryArticle.contentType,
                      click_url: hrefArticle,
                      nav_link_section: 'Article',
                      track_event: 'link_click',
                      click_type: 'Link Click',
                      event_action: 'link_click',
                      event_type: 'link_click',
                      sectionVariationObject,
                    }}
                    className={styles['card-link']}
                    data-testid="secondary-article-title-link"
                  >
                    <Typography
                      variant="headingSm"
                      content={secondaryArticle.title}
                      data-testid="secondary-article-title"
                    />
                  </AchieveLink>
                </div>
              </div>
            </Grid>
          )
        })}
      </Grid>
    )
  }

  return (
    <Section
      className={styles['main-articles-category']}
      data-background={config?.backgroundSection}
      data-testid="main-list-articles-category-section"
    >
      <Grid container className={styles['main-articles-title']}>
        <Grid size={{ xs: 12 }}>
          <Typography
            content={title}
            variant="headingMd"
            data-testid="main-list-articles-category-title"
          />
        </Grid>
        <Grid size={{ xs: 12 }} data-testid="nav-pills-container">
          <NavPills
            content={getNavItems}
            activeSection={activeSection}
            setActiveSection={(index) => {
              dispatch({
                type: 'setActiveSection',
                payload: index,
              })
            }}
            trackData={{ content_type: articles?.[0]?.contentType }}
          />
        </Grid>
      </Grid>
      {renderMainArticle()}
      {renderListArticles()}
      <Grid container>
        <Grid size={{ xs: 12 }} textAlign={'center'}>
          <LinkButton
            size="small"
            content={link}
            variant="contained"
            data-testid="main-articles-button"
            typographicOptions={{ variant: 'labelMd' }}
            className={styles['main-articles-button']}
          />
        </Grid>
      </Grid>
    </Section>
  )
}
export { MainListArticlesCategory }
export default MainListArticlesCategory
